import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';

import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';

import { mcpChatApiRef } from '../../api';
import type { ChatMessage as ApiChatMessage, StreamEvent } from '../../types';
import { ChatMessage } from './ChatMessage';
import { QuickStart } from './QuickStart';
import { TypingIndicator } from './TypingIndicator';
import { Textfield } from '@xlrt/components';
import {
  SendIconDisabled,
  SendIconEnabled,
  StopIcon,
} from '../../assets/Icons/Icons';
import { useChatContainerStyles } from './chat-container.styles';
import { mcpChatTranslationRef } from '../../translation';
import Typography from '@mui/material/Typography';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  tools?: string[];
  toolsUsed?: string[];
  toolResponses?: any[];
}

interface MCPServer {
  id: string;
  name: string;
  enabled: boolean;
  type?: string;
  hasUrl?: boolean;
  hasNpxCommand?: boolean;
  hasScriptPath?: boolean;
}

interface ChatContainerProps {
  sidebarCollapsed: boolean;
  mcpServers: MCPServer[];
  messages: Message[];
  onMessagesChange: (messages: Message[]) => void;
  conversationId?: string;
  onConversationUpdated?: (conversationId: string) => void;
  onPendingPromptChange?: (
    prompt: string | undefined,
    cancelRequest?: () => void,
  ) => void;
  hideRightPaneMargin?: boolean;
  compactInput?: boolean;
  disabled?: boolean;
}

export interface ChatContainerRef {
  cancelOngoingRequest: () => void;
  sendMessage: (messageText: string) => void;
}

export const ChatContainer = forwardRef<ChatContainerRef, ChatContainerProps>(
  (
    {
      mcpServers,
      messages,
      onMessagesChange,
      conversationId,
      onConversationUpdated,
      onPendingPromptChange,
      compactInput = false,
      disabled = false,
    },
    ref,
  ) => {
    const theme = useTheme();
    const mcpChatApi = useApi(mcpChatApiRef);
    const configApi = useApi(configApiRef);
    const streamingEnabled =
      configApi.getOptionalBoolean('mcpChat.streaming.enabled') ?? true;
    const classes = useChatContainerStyles({});
    const { t } = useTranslationRef(mcpChatTranslationRef);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [streamingText, setStreamingText] = useState('');
    const [isMultiline, setIsMultiline] = useState(false);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const streamingTextRef = useRef('');
    const inputRef = useRef<HTMLInputElement>(null);

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Function to cancel ongoing requests
    const cancelOngoingRequest = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsTyping(false);
        onPendingPromptChange?.(undefined);
      }
    };

    useEffect(() => {
      scrollToBottom();
    }, [messages, streamingText]);

    // Keep input focused when not typing (bot responding)
    useEffect(() => {
      if (!isTyping) {
        inputRef.current?.focus();
      }
    }, [isTyping, messages]);

    // Cleanup: cancel any ongoing requests when component unmounts
    useEffect(() => {
      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, []);

    const measureMultiline = (el: HTMLTextAreaElement) => {
      const style = window.getComputedStyle(el);
      const lineHeight =
        parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
      const padding =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const lines = Math.round((el.scrollHeight - padding) / lineHeight);
      setIsMultiline(lines > 1);
    };

    // Simple (non-streaming) API call
    const sendWithSimpleApi = async (
      apiMessages: ApiChatMessage[],
      enabledTools: string[],
    ): Promise<{ message: Message; conversationId?: string }> => {
      const response = await mcpChatApi.sendChatMessage(
        apiMessages,
        enabledTools,
        abortControllerRef.current!.signal,
        conversationId,
      );

      return {
        message: {
          id: (Date.now() + 1).toString(),
          text: response.content,
          isUser: false,
          timestamp: new Date(),
          tools: response.toolResponses?.map(tool => tool.toolName) || [],
          toolsUsed: response.toolsUsed || [],
          toolResponses: response.toolResponses || [],
        },
        conversationId: response.conversationId,
      };
    };

    // Streaming API call
    const sendWithStreaming = async (
      apiMessages: ApiChatMessage[],
      enabledTools: string[],
    ): Promise<{ message: Message; conversationId?: string }> => {
      const toolsUsed: string[] = [];
      const toolResponses: any[] = [];
      let savedConversationId: string | undefined;

      const handleStreamEvent = (event: StreamEvent) => {
        switch (event.type) {
          case 'token':
            streamingTextRef.current += event.content;
            setStreamingText(streamingTextRef.current);
            break;
          case 'tool_start':
            toolsUsed.push(event.name);
            break;
          case 'tool_done':
            toolResponses.push({
              toolName: event.name,
              result: event.result,
            });
            break;
          case 'conversation_saved':
            savedConversationId = event.conversationId;
            break;
          case 'ai_metadata':
            break;
          case 'error':
            streamingTextRef.current += `\n\nError: ${event.message}`;
            setStreamingText(streamingTextRef.current);
            break;
          default:
            break;
        }
      };

      await mcpChatApi.sendChatMessageStream(
        apiMessages,
        enabledTools,
        handleStreamEvent,
        abortControllerRef.current!.signal,
        conversationId,
      );

      return {
        message: {
          id: (Date.now() + 1).toString(),
          text: streamingTextRef.current,
          isUser: false,
          timestamp: new Date(),
          tools: toolResponses.map(tool => tool.toolName),
          toolsUsed,
          toolResponses,
        },
        conversationId: savedConversationId,
      };
    };

    const sendMessageToAPI = async (messageText: string) => {
      if (disabled) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      onPendingPromptChange?.(messageText, cancelOngoingRequest);

      const newMessage: Message = {
        id: Date.now().toString(),
        text: messageText,
        isUser: true,
        timestamp: new Date(),
      };
      onMessagesChange([...messages, newMessage]);
      setIsTyping(true);
      setStreamingText('');
      streamingTextRef.current = '';

      try {
        const apiMessages: ApiChatMessage[] = [
          ...messages.map(msg => ({
            role: msg.isUser ? ('user' as const) : ('assistant' as const),
            content: msg.text,
          })),
          {
            role: 'user' as const,
            content: messageText,
          },
        ];

        const enabledTools = mcpServers
          .filter(server => server.enabled)
          .map(server => server.id);

        let botResponse: Message;
        let savedConversationId: string | undefined;

        if (streamingEnabled) {
          const result = await sendWithStreaming(apiMessages, enabledTools);
          botResponse = result.message;
          savedConversationId = result.conversationId;
        } else {
          const result = await sendWithSimpleApi(apiMessages, enabledTools);
          botResponse = result.message;
          savedConversationId = result.conversationId;
        }

        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setIsTyping(false);
        setStreamingText('');
        streamingTextRef.current = '';
        abortControllerRef.current = null;

        if (savedConversationId && onConversationUpdated) {
          onConversationUpdated(savedConversationId);
        }

        onMessagesChange([...messages, newMessage, botResponse]);
        onPendingPromptChange?.(undefined);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Request was cancelled');
          setStreamingText('');
          streamingTextRef.current = '';
          return;
        }

        setIsTyping(false);
        setStreamingText('');
        streamingTextRef.current = '';
        abortControllerRef.current = null;
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', err);

        let errorMessage: string = t('chatContainer.errors.defaultError');

        if (err instanceof Error) {
          if (err.message.includes('404')) {
            errorMessage = t('chatContainer.errors.serviceUnavailable');
          } else if (err.message.includes('Network')) {
            errorMessage = t('chatContainer.errors.networkError');
          } else {
            errorMessage = `Error: ${err.message}`;
          }
        }

        const errorResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: errorMessage,
          isUser: false,
          timestamp: new Date(),
          tools: [],
          toolsUsed: [],
          toolResponses: [],
        };
        onMessagesChange([...messages, newMessage, errorResponse]);
        onPendingPromptChange?.(undefined);
      }
    };

    useImperativeHandle(ref, () => ({
      cancelOngoingRequest,
      sendMessage: messageText => {
        void sendMessageToAPI(messageText);
      },
    }));

    const handleSuggestionClick = async (suggestion: string) => {
      await sendMessageToAPI(suggestion);
    };

    const handleSendMessage = async () => {
      if (inputValue.trim()) {
        const messageText = inputValue;
        setInputValue('');
        setIsMultiline(false);
        await sendMessageToAPI(messageText);
      }
    };

    const handleRetry = async (messageId: string) => {
      const messageIndex = messages.findIndex(m => m.id === messageId);
      if (messageIndex <= 0) return;

      const userMessage = messages[messageIndex - 1];
      if (!userMessage || !userMessage.isUser) return;

      const updatedMessages = messages.slice(0, messageIndex);
      onMessagesChange(updatedMessages);
      await sendMessageToAPI(userMessage.text);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    };

    const effectiveMultiline =
      !compactInput &&
      (messages.length === 0 || (inputValue.trim() !== '' && isMultiline));
    const hasSentPrompt = messages.some(message => message.isUser);
    const defaultInputBottom =
      messages.length === 0 ? theme.spacing(2) : theme.spacing(6);
    const inputBottom = compactInput ? '16px' : defaultInputBottom;
    const inputMargin = !compactInput && messages.length === 0 ? '0 auto' : 0;
    const defaultMinRows = messages.length === 0 ? 5 : 1.15;
    const inputMinRows = compactInput ? 1 : defaultMinRows;

    const inputPlaceholder = compactInput
      ? 'Describe what you would like to achieve'
      : t('chatContainer.inputPlaceholder');

    return (
      <Box
        className={`${classes.contentWrapper} ${
          messages.length === 0 ? classes.quickStart : classes.chatStarted
        }`}
      >
        {messages.length === 0 ? (
          <QuickStart
            onSuggestionClick={handleSuggestionClick}
            compact={compactInput}
          />
        ) : (
          <Box
            className={`${classes.chatcontainer} ${
              compactInput ? classes.compactChatcontainer : ''
            }`}
          >
            {messages.map(message => (
              <ChatMessage
                key={message.id}
                message={message}
                onRetry={handleRetry}
                compact={compactInput}
              />
            ))}
            {isTyping && streamingText && (
              <ChatMessage
                key="streaming-response"
                message={{
                  id: 'streaming',
                  text: streamingText,
                  isUser: false,
                  timestamp: new Date(),
                }}
                onRetry={() => {}}
                compact={compactInput}
              />
            )}
            {isTyping && !streamingText && (
              <TypingIndicator compact={compactInput} />
            )}
            <div ref={messagesEndRef} />
          </Box>
        )}

        <Textfield
          ref={inputRef}
          className={`${classes.inputFieldContainer} ${
            compactInput ? classes.compactInputFieldContainer : ''
          } ${effectiveMultiline ? classes.multilineInput : ''}`}
          style={{
            position:
              compactInput || messages.length > 0 ? 'absolute' : 'relative',
            bottom: inputBottom,
            left: '50%',
            borderRadius: compactInput ? '16px' : '1000px',
            transform: 'translateX(-50%)',
            width: compactInput ? 'calc(100% - 32px)' : '94%',
            margin: inputMargin,
            marginBottom:
              !compactInput && messages.length === 0 ? theme.spacing(6) : 0,
            zIndex: 1000,
            transition: 'all 0.2s ease',
          }}
          placeholder={isTyping ? '' : inputPlaceholder}
          variant="outlined"
          multiline
          minRows={inputMinRows}
          maxRows={compactInput ? 6 : 8}
          value={inputValue}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            setInputValue(e.target.value);
            if (!compactInput && e.target instanceof HTMLTextAreaElement) {
              measureMultiline(e.target);
            }
          }}
          onKeyDown={handleKeyDown}
          size="small"
          disabled={disabled || isTyping}
          color="primary"
          InputProps={{
            endAdornment: (
              <Box
                sx={
                  compactInput
                    ? {
                        position: 'absolute',
                        right: '8px',
                        bottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    : undefined
                }
              >
                <IconButton
                  data-testid="send-button"
                  sx={{
                    padding: compactInput ? 0 : undefined,
                    ...(compactInput ? {} : { paddingTop: 0 }),
                    width: compactInput ? '28px' : undefined,
                    height: compactInput ? '28px' : undefined,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  onClick={isTyping ? cancelOngoingRequest : handleSendMessage}
                  disabled={disabled || (!isTyping && !inputValue.trim())}
                  disableRipple
                >
                  {isTyping && <StopIcon />}
                  {!isTyping && inputValue.trim() && <SendIconEnabled />}
                  {!isTyping && !inputValue.trim() && <SendIconDisabled />}
                </IconButton>
              </Box>
            ),
          }}
        />
        {hasSentPrompt && (
          <Typography
            className={`${classes.disclaimer} ${
              compactInput ? classes.compactDisclaimer : ''
            }`}
          >
            {t('disclaimer.message')}
          </Typography>
        )}
      </Box>
    );
  },
);
